/**
 * @file schemas.ts
 * @brief Zod schemas + TS types for the Super Admin → Companies feature.
 *
 * @details
 * Mirrors the response shapes produced by `BACKEND/src/features/admin/`:
 * - `service.js::getAllCompanies` returns `{ companies, pagination }`. Each
 *   row is the raw `companies.*` SQL row plus the aggregated columns
 *   `user_count`, `session_count`, `total_revenue_cents`.
 * - `service.js::createCompany` accepts `{ name, domain, billing_email, … }`
 *   and returns the created row.
 *
 * Custom per-company rates are stored in **minor units** (cents/pence),
 * to match the `total_cost_cents` / `*_minor` semantics used everywhere
 * in the backend. Forms display major units (£/€/$) and convert at the
 * boundary.
 *
 * @references
 * - BACKEND/src/features/admin/service.js (getAllCompanies, createCompany,
 *   updateCompany, suspendCompany, reactivateCompany, deleteCompany)
 * - BACKEND/src/features/admin/routes.js  (route shapes)
 * - BACKEND/src/db/schema.sql              (companies table)
 *
 * @author DES
 * @date 26-05-2026
 */

import { z } from "zod";

// ----------------------------------------------------------------------
// Status enum — companies share the same lifecycle as users:
// active / suspended / deleted (see BACKEND/src/db/schema.sql).
// ----------------------------------------------------------------------
export const CompanyStatusSchema = z.enum(["active", "suspended", "deleted"]);
export type CompanyStatus = z.infer<typeof CompanyStatusSchema>;

// ----------------------------------------------------------------------
// Numeric coercion — Postgres COUNT(*) and SUM() come back as strings
// over the wire, so accept either and normalize to number.
// ----------------------------------------------------------------------
const numericString = z.union([
  z.number(),
  z
    .string()
    .transform((v) => (v === "" ? 0 : Number(v)))
    .pipe(z.number()),
]);

const optionalIntFromInput = z.union([
  z.number().int(),
  z
    .string()
    .transform((v) => (v.trim() === "" ? undefined : Number(v)))
    .pipe(z.number().int().optional()),
  z.undefined(),
]);

// ----------------------------------------------------------------------
// Admin company row (list endpoint)
// `passthrough()` so backend additions (audit columns, soft-delete
// metadata, etc.) don't break parsing on every backend deploy.
// ----------------------------------------------------------------------
export const AdminCompanySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    domain: z.string().nullable().optional(),
    billing_email: z.string().nullable().optional(),
    address: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    state: z.string().nullable().optional(),
    postal_code: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    phone_number: z.string().nullable().optional(),
    tax_id: z.string().nullable().optional(),

    // Legacy columns may still appear on API rows; ignored by product (26-07-2026).

    custom_rate_usd_minor: numericString.nullable().optional(),
    custom_rate_eur_minor: numericString.nullable().optional(),
    custom_rate_gbp_minor: numericString.nullable().optional(),

    // Per-product company overrides (06-07-2026). NULL → platform rate.
    custom_min_usd_minor: numericString.nullable().optional(),
    custom_min_eur_minor: numericString.nullable().optional(),
    custom_min_gbp_minor: numericString.nullable().optional(),
    custom_realtime_rate_usd_minor: numericString.nullable().optional(),
    custom_realtime_rate_eur_minor: numericString.nullable().optional(),
    custom_realtime_rate_gbp_minor: numericString.nullable().optional(),
    custom_realtime_min_usd_minor: numericString.nullable().optional(),
    custom_realtime_min_eur_minor: numericString.nullable().optional(),
    custom_realtime_min_gbp_minor: numericString.nullable().optional(),
    custom_boardroom_rate_usd_minor: numericString.nullable().optional(),
    custom_boardroom_rate_eur_minor: numericString.nullable().optional(),
    custom_boardroom_rate_gbp_minor: numericString.nullable().optional(),
    custom_boardroom_speaker_fee_usd_minor: numericString.nullable().optional(),
    custom_boardroom_speaker_fee_eur_minor: numericString.nullable().optional(),
    custom_boardroom_speaker_fee_gbp_minor: numericString.nullable().optional(),
    custom_note_rate_usd_minor: numericString.nullable().optional(),
    custom_note_rate_eur_minor: numericString.nullable().optional(),
    custom_note_rate_gbp_minor: numericString.nullable().optional(),
    custom_note_min_usd_minor: numericString.nullable().optional(),
    custom_note_min_eur_minor: numericString.nullable().optional(),
    custom_note_min_gbp_minor: numericString.nullable().optional(),

    status: CompanyStatusSchema.catch("active"),
    deleted_at: z.string().nullable().optional(),
    created_at: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),

    /* Aggregations — see service.js::getAllCompanies SQL */
    user_count: numericString.optional().default(0),
    session_count: numericString.optional().default(0),
    total_revenue_cents: numericString.optional().default(0),

    /* Seat / pool summary (26-07-2026) — enterprise shared or team individual */
    plan_kind: z
      .enum(["none", "enterprise", "team"])
      .optional()
      .default("none"),
    plan_code: z.string().nullable().optional(),
    plan_status: z.string().nullable().optional(),
    pool_mode: z.enum(["shared", "individual"]).nullable().optional(),
    plan_label: z.string().optional(),
    included_minutes: numericString.nullable().optional(),
    used_minutes: numericString.nullable().optional(),
    remaining_minutes: numericString.nullable().optional(),
    seats: numericString.nullable().optional(),
    seats_used: numericString.nullable().optional(),
    minutes_per_seat: numericString.nullable().optional(),
  })
  .passthrough();

export type AdminCompany = z.infer<typeof AdminCompanySchema>;

// ----------------------------------------------------------------------
// Pagination envelope
// ----------------------------------------------------------------------
export const AdminPaginationSchema = z.object({
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  totalPages: z.number(),
});

export const AdminCompaniesListResponseSchema = z.object({
  companies: z.array(AdminCompanySchema),
  pagination: AdminPaginationSchema,
});

export type AdminCompaniesListResponse = z.infer<
  typeof AdminCompaniesListResponseSchema
>;

// ----------------------------------------------------------------------
// List query params (sent to GET /api/admin/companies)
// ----------------------------------------------------------------------
export interface AdminCompaniesListParams {
  page?: number;
  limit?: number;
  status?: CompanyStatus;
  search?: string;
}

// ----------------------------------------------------------------------
// Create input — fields accepted by POST /api/admin/companies.
// `name` is the only required field; everything else is optional and
// can be filled later via Edit.
// ----------------------------------------------------------------------
export const CreateCompanyInputSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),

  domain: z.string().optional().or(z.literal("")),
  billing_email: z
    .string()
    .email("Enter a valid email")
    .optional()
    .or(z.literal("")),

  address: z.string().optional().or(z.literal("")),
  city: z.string().optional().or(z.literal("")),
  state: z.string().optional().or(z.literal("")),
  postal_code: z.string().optional().or(z.literal("")),
  country: z.string().optional().or(z.literal("")),

  phone_number: z.string().optional().or(z.literal("")),
  tax_id: z.string().optional().or(z.literal("")),

  custom_rate_usd_minor: optionalIntFromInput,
  custom_rate_eur_minor: optionalIntFromInput,
  custom_rate_gbp_minor: optionalIntFromInput,

  // Per-product company overrides (06-07-2026).
  custom_min_usd_minor: optionalIntFromInput,
  custom_min_eur_minor: optionalIntFromInput,
  custom_min_gbp_minor: optionalIntFromInput,
  custom_realtime_rate_usd_minor: optionalIntFromInput,
  custom_realtime_rate_eur_minor: optionalIntFromInput,
  custom_realtime_rate_gbp_minor: optionalIntFromInput,
  custom_realtime_min_usd_minor: optionalIntFromInput,
  custom_realtime_min_eur_minor: optionalIntFromInput,
  custom_realtime_min_gbp_minor: optionalIntFromInput,
  custom_boardroom_rate_usd_minor: optionalIntFromInput,
  custom_boardroom_rate_eur_minor: optionalIntFromInput,
  custom_boardroom_rate_gbp_minor: optionalIntFromInput,
  custom_boardroom_speaker_fee_usd_minor: optionalIntFromInput,
  custom_boardroom_speaker_fee_eur_minor: optionalIntFromInput,
  custom_boardroom_speaker_fee_gbp_minor: optionalIntFromInput,
  custom_note_rate_usd_minor: optionalIntFromInput,
  custom_note_rate_eur_minor: optionalIntFromInput,
  custom_note_rate_gbp_minor: optionalIntFromInput,
  custom_note_min_usd_minor: optionalIntFromInput,
  custom_note_min_eur_minor: optionalIntFromInput,
  custom_note_min_gbp_minor: optionalIntFromInput,
});

export type CreateCompanyInput = z.infer<typeof CreateCompanyInputSchema>;

// ----------------------------------------------------------------------
// Update input — every field optional; sent to PUT /api/admin/companies/:id
// (status changes here cascade to all employees per
// service.js::updateCompany).
// ----------------------------------------------------------------------
export const UpdateCompanyInputSchema = CreateCompanyInputSchema.partial().extend({
  status: CompanyStatusSchema.optional(),
});

export type UpdateCompanyInput = z.infer<typeof UpdateCompanyInputSchema>;
